import { Dimensions, DimensionValue, TextStyle, ViewStyle } from 'react-native';
import { stylesMap } from './stylesMap';
import { CSSProperties } from 'react'; // Ensure React types are available
import { ColorValue } from 'react-native';

type ClassName = keyof typeof stylesMap;

const { width: screenWidth, height: screenHeight } = Dimensions.get('screen');
const { width: windowWidth, height: windowHeight } = Dimensions.get('window');

export const useNativeStyleX = (classes: string[]): ViewStyle | TextStyle => {

    return classes.reduce((acc: ViewStyle | TextStyle, cls) => {
        let style: ViewStyle | TextStyle | undefined;


        const match = cls.match(/^([\w-]+)\[(.+)\]$/); // Matches "fs-[11]", "bg-[#ff0000]"
        const fontMatch = cls.match(/^font-\[(.*?)\]$/);
        // const regex = /([wh]-)\[([^\]]+)\]-(screen|window)/;
        const regex = /^(w|h)-\[[^\]]+\]-(screen|window)$/;
        const matchEndPart = cls.match(regex);

        if (fontMatch) {
            const fontFamily = fontMatch[1]; // Extract font name from []
            return { ...acc, fontFamily: fontFamily || 'sans-serif' }; // Fallback to 'sans-serif'
        }

        if (matchEndPart) {

            const prefix = matchEndPart[1];   // "w-" or "h-"
            const value = matchEndPart[0].match(/\[([^\]]+)\]/)[1];
            const suffix = matchEndPart[2];

            if ((prefix === 'w' || prefix === 'h') && (suffix === 'screen' || suffix === 'window')) {
                // console.log('Last:', suffix, prefix);

                const [num, denom] = value.split(/[/\*\-]/).map(Number)
                // console.log('Apply:', typeof screenWidth, typeof denom);

                // Apply calculated width or height
                style = { width: 360 / 2 };
            }
        } else{
            style = stylesMap[cls as ClassName];
        }

        if (match) {
            const key = match[1]; // Extracts Ex: "fs-", "bg-"
            const value = match[2]; // Extracts Ex: "11", "#ff0000"
            const dimensionValue: DimensionValue = isNaN(Number(value)) ? (value as DimensionValue) : (Number(value) as DimensionValue);


            if (key === 'bg-') {
                style = { backgroundColor: value as ColorValue };
            } else if (key === 'text-') {
                style = { color: value as ColorValue };
            } else if (key === 'w-') {
                style = { width: isNaN(Number(value)) ? (value as DimensionValue) : (Number(value) as DimensionValue) };
            } else if (key === 'h-') {
                style = { height: isNaN(Number(value)) ? (value as DimensionValue) : (Number(value) as DimensionValue) };
            } else if (key === 'minW-') {
                style = { minWidth: Number(value) };
            } else if (key === 'maxW-') {
                style = { maxWidth: Number(value) };
            } else if (key === 'minH-') {
                style = { minHeight: Number(value) };
            } else if (key === 'maxH-') {
                style = { maxHeight: Number(value) };
            } else if (key === 'font-') {
                style = { fontSize: Number(value) };
            } else if (key === 'border-') {
                style = { borderWidth: Number(value) };
            } else if (key === 'border-color-') {
                style = { borderColor: value as ColorValue };
            } else if (key === 'borderOpacity-') {
                style = { borderColor: `rgba(0,0,0,${Number(value as ColorValue)})` };
            } else if (key === 'borderTop-') {
                style = { borderTopWidth: Number(value) };
            } else if (key === 'borderBottom-') {
                style = { borderBottomWidth: Number(value) };
            } else if (key === 'borderLeft-') {
                style = { borderLeftWidth: Number(value) };
            } else if (key === 'borderRight-') {
                style = { borderRightWidth: Number(value) };
            } else if (key === 'rounded-') {
                style = { borderRadius: Number(value) };
            } else if (key === 'm-') {
                style = { margin: dimensionValue };
            } else if (key === 'mt-') {
                style = { marginTop: dimensionValue };
            } else if (key === 'mb-') {
                style = { marginBottom: dimensionValue };
            } else if (key === 'ml-') {
                style = { marginLeft: dimensionValue };
            } else if (key === 'mr-') {
                style = { marginRight: dimensionValue };
            } else if (key === 'my-') {
                style = { marginTop: dimensionValue, marginBottom: dimensionValue };
            } else if (key === 'mx-') {
                style = { marginLeft: dimensionValue, marginRight: dimensionValue };
            } else if (key === 'p-') {
                style = { padding: dimensionValue };
            } else if (key === 'pt-') {
                style = { paddingTop: dimensionValue };
            } else if (key === 'pb-') {
                style = { paddingBottom: dimensionValue };
            } else if (key === 'pl-') {
                style = { paddingLeft: dimensionValue };
            } else if (key === 'pr-') {
                style = { paddingRight: dimensionValue };
            } else if (key === 'py-') {
                style = { paddingTop: dimensionValue, paddingBottom: dimensionValue };
            } else if (key === 'px-') {
                style = { paddingLeft: dimensionValue, paddingRight: dimensionValue };
            } else if (key === 'top-') {
                style = { top: Number(value) };
            } else if (key === 'right-') {
                style = { right: Number(value) };
            } else if (key === 'bottom-') {
                style = { bottom: Number(value) };
            } else if (key === 'vertical-') {
                style = { verticalAlign: value as "auto" | "top" | "bottom" | "middle" };
            } else if (key === 'left-') {
                style = { left: Number(value) };
            } else if (key === 'opacity-') {
                style = { opacity: Number(value) / 100 };
            } else if (key === 'gap-') {
                style = { gap: Number(value) };
            } else if (key === 'col-gap-') {
                style = { columnGap: isNaN(Number(value)) ? value : Number(value) };
            } else if (key === 'row-gap-') {
                style = { rowGap: isNaN(Number(value)) ? value : Number(value) };
            } else if (key === 'elevation-') {
                style = { elevation: Number(value) };
            } else if (key === 'flex-') {
                style = { flex: Number(value) };
                // } else if (key === 'touch-effect') {
                //     style.transform = [{ scale }];
                //     style.onPressIn = () =>
                //         Animated.timing(scale, {
                //             toValue: 0.95,
                //             duration: 100,
                //             useNativeDriver: true,
                //         }).start();
                //     style.onPressOut = () =>
                //         Animated.timing(scale, {
                //             toValue: 1,
                //             duration: 100,
                //             useNativeDriver: true,
                //         }).start();
            } else if (!isNaN(Number(value))) {
                const dynamicStyle = stylesMap[key as ClassName];
                if (typeof dynamicStyle === 'function') {
                    style = dynamicStyle(Number(value)); // ✅ Convert numeric values
                }
            } else {
                const dynamicStyle = stylesMap[key as ClassName];
                if (typeof dynamicStyle === 'function') {
                    style = dynamicStyle(value); // ✅ Keep string values (e.g., percentages)
                }
            }
        } else {
            style = stylesMap[cls as ClassName];
        }

        // if (style) acc.push(style);
        // return acc;
        return style ? { ...acc, ...style } : acc;
    }, {});

};


export const getStyles = (classes: string[]): ViewStyle[] => {
    return classes.map(cls => {
        const style = stylesMap[cls as ClassName];
        if (style) {
            return style;
        }
        return {};  // Return an empty object if the class is not found
    });
};



export const testingNative = (classes: string[]): ViewStyle => {
    return classes.reduce((acc: ViewStyle, cls) => {
        let style: ViewStyle | undefined;

        const match = cls.match(/^([\w-]+)\[(.+)\]$/); // Matches dynamic classes e.g., "fs-[11]", "bg-[#ff0000]"
        if (match) {
            const key = match[1]; // Extracts "fs-", "bg-"
            const value = match[2]; // Extracts "11", "#ff0000"

            if (key in stylesMap) {
                const dynamicStyle = stylesMap[key as ClassName];
                if (typeof dynamicStyle === 'function') {
                    style = dynamicStyle(isNaN(Number(value)) ? value : Number(value));
                }
            }
        } else {
            style = stylesMap[cls as ClassName];
        }

        // if (style) acc.push(style);
        // return acc;
        return style ? { ...acc, ...style } : acc;
    }, {});
};




// import { stylesMap } from './stylesMap';
// import { CSSProperties } from 'react'; // Ensure React types are available

// type ClassName = keyof typeof stylesMap;

// export const useNativeStyleX = (classes: string[]): CSSProperties => {

//     return classes.reduce((acc: CSSProperties, cls) => {
//         let style: CSSProperties | undefined;

//         const match = cls.match(/^([\w-]+)\[(.+)\]$/); // Matches "fs-[11]", "bg-[#ff0000]"
//         const fontMatch = cls.match(/^font-\[(.*?)\]$/);

//         if (fontMatch) {
//             const fontFamily = fontMatch[1]; // Extract font name from []
//             return { ...acc, fontFamily: fontFamily || 'sans-serif' }; // Fallback to 'sans-serif'
//         }

//         if (match) {
//             const key = match[1]; // Extracts Ex: "fs-", "bg-"
//             const value = match[2]; // Extracts Ex: "11", "#ff0000"

//             if (key === 'bg-') {
//                 style = { backgroundColor: value };
//             } else if (key === 'text-') {
//                 style = { color: value };
//             } else if (key === 'w-') {
//                 style = { width: isNaN(Number(value)) ? value : Number(value) };
//             } else if (key === 'h-') {
//                 style = { height: isNaN(Number(value)) ? value : Number(value) };
//             } else if (key === 'minW-') {
//                 style = { minWidth: Number(value) };
//             } else if (key === 'maxW-') {
//                 style = { maxWidth: Number(value) };
//             } else if (key === 'minH-') {
//                 style = { minHeight: Number(value) };
//             } else if (key === 'maxH-') {
//                 style = { maxHeight: Number(value) };
//             } else if (key === 'font-') {
//                 style = { fontSize: Number(value) };
//             } else if (key === 'border-') {
//                 style = { borderWidth: Number(value) };
//             } else if (key === 'border-color-') {
//                 style = { borderColor: value };
//             } else if (key === 'borderOpacity-') {
//                 style = { borderColor: `rgba(0,0,0,${Number(value)})` };
//             } else if (key === 'borderTop-') {
//                 style = { borderTopWidth: Number(value) };
//             } else if (key === 'borderBottom-') {
//                 style = { borderBottomWidth: Number(value) };
//             } else if (key === 'borderLeft-') {
//                 style = { borderLeftWidth: Number(value) };
//             } else if (key === 'borderRight-') {
//                 style = { borderRightWidth: Number(value) };
//             } else if (key === 'rounded-') {
//                 style = { borderRadius: Number(value) };
//             } else if (key === 'm-') {
//                 style = { margin: isNaN(Number(value)) ? value : Number(value) };
//             } else if (key === 'mt-') {
//                 style = { marginTop: isNaN(Number(value)) ? value : Number(value) };
//             } else if (key === 'mb-') {
//                 style = { marginBottom: value };
//             } else if (key === 'ml-') {
//                 style = { marginLeft: value };
//             } else if (key === 'mr-') {
//                 style = { marginRight: value };
//             } else if (key === 'my-') {
//                 style = { marginTop: isNaN(Number(value)) ? value : Number(value), marginBottom: isNaN(Number(value)) ? value : Number(value) };
//             } else if (key === 'mx-') {
//                 style = { marginLeft: value, marginRight: value };
//             } else if (key === 'p-') {
//                 style = { padding: value };
//             } else if (key === 'pt-') {
//                 style = { paddingTop: value };
//             } else if (key === 'pb-') {
//                 style = { paddingBottom: value };
//             } else if (key === 'pl-') {
//                 style = { paddingLeft: value };
//             } else if (key === 'pr-') {
//                 style = { paddingRight: value };
//             } else if (key === 'py-') {
//                 style = { paddingTop: isNaN(Number(value)) ? value : Number(value), paddingBottom: isNaN(Number(value)) ? value : Number(value) };
//             } else if (key === 'px-') {
//                 style = { paddingLeft: isNaN(Number(value)) ? value : Number(value), paddingRight: isNaN(Number(value)) ? value : Number(value) };
//             } else if (key === 'top-') {
//                 style = { top: Number(value) };
//             } else if (key === 'right-') {
//                 style = { right: Number(value) };
//             } else if (key === 'bottom-') {
//                 style = { bottom: Number(value) };
//             } else if (key === 'vertical-') {
//                 style = { verticalAlign: value };
//             } else if (key === 'left-') {
//                 style = { left: Number(value) };
//             } else if (key === 'opacity-') {
//                 style = { opacity: Number(value) / 100 };
//             } else if (key === 'gap-') {
//                 style = { gap: Number(value) };
//             } else if (key === 'col-gap-') {
//                 style = { columnGap: isNaN(Number(value)) ? value : Number(value) };
//             } else if (key === 'row-gap-') {
//                 style = { rowGap: isNaN(Number(value)) ? value : Number(value) };
//             } else if (key === 'elevation-') {
//                 style = { ele: value };
//             } else if (key === 'flex-') {
//                 style = { flex: Number(value) };
//             // } else if (key === 'touch-effect') {
//             //     style.transform = [{ scale }];
//             //     style.onPressIn = () =>
//             //         Animated.timing(scale, {
//             //             toValue: 0.95,
//             //             duration: 100,
//             //             useNativeDriver: true,
//             //         }).start();
//             //     style.onPressOut = () =>
//             //         Animated.timing(scale, {
//             //             toValue: 1,
//             //             duration: 100,
//             //             useNativeDriver: true,
//             //         }).start();
//             } else if (!isNaN(Number(value))) {
//                 const dynamicStyle = stylesMap[key as ClassName];
//                 if (typeof dynamicStyle === 'function') {
//                     style = dynamicStyle(Number(value)); // ✅ Convert numeric values
//                 }
//             } else {
//                 const dynamicStyle = stylesMap[key as ClassName];
//                 if (typeof dynamicStyle === 'function') {
//                     style = dynamicStyle(value); // ✅ Keep string values (e.g., percentages)
//                 }
//             }
//         } else {
//             style = stylesMap[cls as ClassName];
//         }

//         // if (style) acc.push(style);
//         // return acc;
//         return style ? { ...acc, ...style } : acc;
//     }, {});

// };


// export const getStyles = (classes: string[]): CSSProperties[] => {
//     return classes.map(cls => {
//         const style = stylesMap[cls as ClassName];
//         if (style) {
//             return style;
//         }
//         return {};  // Return an empty object if the class is not found
//     });
// };



// export const testingNative = (classes: string[]): CSSProperties => {
//     return classes.reduce((acc: CSSProperties, cls) => {
//         let style: CSSProperties | undefined;

//         const match = cls.match(/^([\w-]+)\[(.+)\]$/); // Matches dynamic classes e.g., "fs-[11]", "bg-[#ff0000]"
//         if (match) {
//             const key = match[1]; // Extracts "fs-", "bg-"
//             const value = match[2]; // Extracts "11", "#ff0000"

//             if (key in stylesMap) {
//                 const dynamicStyle = stylesMap[key as ClassName];
//                 if (typeof dynamicStyle === 'function') {
//                     style = dynamicStyle(isNaN(Number(value)) ? value : Number(value));
//                 }
//             }
//         } else {
//             style = stylesMap[cls as ClassName];
//         }

//         // if (style) acc.push(style);
//         // return acc;
//         return style ? { ...acc, ...style } : acc;
//     }, {});
// };

